/**
 * Reconstrói, a partir do `BF_SELLER_DATASETS`, o formato que o app sempre consumiu via
 * `getOnDB` / `tables.current`.
 *
 * O banco guarda um registro por arquivo, com o payload normalizado. Este módulo faz o caminho de
 * volta: concatena os arquivos de uma store e refaz os joins que saíram da gravação — preço entra
 * no produto, ciclo de venda entra no cliente, boletos viram mapa por cliente.
 *
 * É o caminho das telas que precisam do conjunto inteiro (carrinho, checkout, cálculo de preço).
 * As listas não passam por aqui: elas usam `bfSellerProducts` / `bfSellerClients`, que montam só
 * a página visível a partir do mesmo dado indexado em memória.
 */

import { toCoded } from '@/shared/coded';
import { ensureClientsGroupedByLoja } from '@/utils/clientDataTransform';
import type { CategoryProps, ClientProps, ProductProps, SalesCycleProps } from '@/types';
import {
  catalogoPorCodigo,
  cicloPorCliente,
  precoPorProduto,
  precoPromocaoPorCliente,
  readFlatRows,
  readSingleObject,
  restricoesPorCliente,
  ultimaCompraPorCliente,
} from './bfSellerCache';
import { readValues } from './bfSellerReader';
import type { BfSellerStore } from './bfSellerSchema';

const isPlainObject = (value: any): value is Record<string, any> =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const asArray = <T = any>(value: any): T[] => (Array.isArray(value) ? value : []);

/** `{ cod_rca, clientes: [...] }` de boletos/cortes → só os nós de cliente. */
const readClienteNodes = async (store: BfSellerStore): Promise<any[]> => {
  const values = await readValues(store);
  return values.flatMap((value) => (Array.isArray(value) ? value : asArray(value?.clientes)));
};

// -------------------------------------------------------------------------------------------
// preço
// -------------------------------------------------------------------------------------------

/**
 * Valor gravado do preço de um produto — `{ [loja]: { [segmento]: { [qtd]: item } } }` — achatado
 * na lista que o `handlePrices` espera em `product.PRECO`.
 */
export const flattenPreco = (produto: number, value: any, bucket: any[] = []): any[] => {
  if (!isPlainObject(value)) return bucket;

  for (const [lojaKey, porSegmento] of Object.entries(value)) {
    if (!isPlainObject(porSegmento)) continue;
    const loja = Number(lojaKey);

    for (const [segmentoKey, embalagens] of Object.entries(porSegmento)) {
      if (!isPlainObject(embalagens)) continue;
      const segmento = Number(segmentoKey);

      for (const [qtdEmbalagem, item] of Object.entries(embalagens)) {
        if (!isPlainObject(item)) continue;
        bucket.push({
          ...item,
          COD_PRODUTO: produto,
          QTD_EMBALAGEM: Number(qtdEmbalagem),
          COD_LOJA: loja,
          NRO_SEGMENTO: segmento,
        });
      }
    }
  }

  return bucket;
};

export const readPrecoByProduto = async (): Promise<Map<number, any[]>> => {
  const porProduto = await precoPorProduto();
  const byProduto = new Map<number, any[]>();
  for (const [produto, value] of porProduto) byProduto.set(produto, flattenPreco(produto, value));
  return byProduto;
};

/** Árvore `[loja][segmento][produto][embalagem]`, que é como o dataset chegava. */
export const hydratePreco = async (): Promise<Record<string, any>> => {
  const porProduto = await precoPorProduto();
  const tree: Record<string, any> = {};

  for (const [produto, value] of porProduto) {
    if (!isPlainObject(value)) continue;

    for (const [loja, porSegmento] of Object.entries(value)) {
      if (!isPlainObject(porSegmento)) continue;
      if (!tree[loja]) tree[loja] = {};

      for (const [segmento, embalagens] of Object.entries(porSegmento)) {
        if (!tree[loja][segmento]) tree[loja][segmento] = {};
        tree[loja][segmento][produto] = embalagens;
      }
    }
  }

  return tree;
};

// -------------------------------------------------------------------------------------------
// produtos
// -------------------------------------------------------------------------------------------

const mergeByKey = <T>(current: T[] | undefined, incoming: T[] | undefined, keyOf: (item: T) => string): T[] => {
  const byKey = new Map<string, T>();
  [...(current || []), ...(incoming || [])].forEach((item) => {
    if (!item) return;
    byKey.set(keyOf(item), item);
  });
  return Array.from(byKey.values());
};

const priceKey = (price: any) =>
  [
    Number(price.COD_LOJA || 0),
    Number(price.NRO_SEGMENTO || 0),
    Number(price.NROCONDICAOPAGTO || 0),
    Number(price.QTD_EMBALAGEM || 0),
    Number(price.QTD_MULTIPLO_VENDA || 0),
    `${price.SG_EMBALAGEM || ''}`,
    `${price.TP_PROMO || ''}`,
    `${price.DT_INICIO || ''}`,
    `${price.DT_FIM || ''}`,
  ].join(':');

const packageKey = (pack: any) =>
  [
    Number(pack.COD_LOJA || 0),
    Number(pack.COD_SEGMENTO || 0),
    Number(pack.QTD_EMBALAGEM || 0),
    Number(pack.QTD_MULTIPLO_VENDA || 0),
    `${pack.SG_EMBALAGEM || ''}`,
  ].join(':');

/** Mesma regra do processamento antigo: embalagem com múltiplo de venda vira `PK`. */
export const normalizeEmbalagem = (embalagem: any): any[] =>
  asArray(embalagem).map((emb) => (Number(emb?.QTD_MULTIPLO_VENDA) > 1 ? { ...emb, SG_EMBALAGEM: 'PK' } : { ...emb }));

/** Monta o produto do jeito que as telas esperam: catálogo + preço + última compra. */
export const buildProduct = (item: any, preco: any[], ultimaCompra: any): ProductProps =>
  ({
    ...item,
    PRECO: preco,
    IMAGEM: '',
    EMBALAGEM: normalizeEmbalagem(item?.EMBALAGEM),
    UNILEVER: item?.FL_UNILEVER === 'True',
    ultimaCompra: ultimaCompra || {},
  }) as ProductProps;

/** O mesmo produto em duas lojas são duas linhas no arquivo; aqui viram um só, com a união. */
export const mergeProducts = (current: ProductProps | undefined, incoming: ProductProps): ProductProps => {
  if (!current) return incoming;
  return {
    ...current,
    ...incoming,
    PRECO: mergeByKey(current.PRECO as any[], incoming.PRECO as any[], priceKey),
    EMBALAGEM: mergeByKey(current.EMBALAGEM as any[], incoming.EMBALAGEM as any[], packageKey),
    ATRIBUTOS: (incoming.ATRIBUTOS?.length ? incoming.ATRIBUTOS : current.ATRIBUTOS) || [],
    IMAGEM: incoming.IMAGEM || current.IMAGEM || '',
    ultimaCompra: incoming.ultimaCompra || current.ultimaCompra || {},
  };
};

/**
 * Última compra por produto, sem olhar de qual cliente — é como o dicionário legado era montado.
 * Quem precisa da compra do cliente certo usa `readUltimaCompraForProdutos`.
 */
export const readUltimaCompraByProduto = async (): Promise<Map<number, any>> => {
  const porCliente = await ultimaCompraPorCliente();
  const byProduto = new Map<number, any>();
  for (const porProduto of porCliente.values()) {
    for (const [produto, row] of Object.entries(porProduto || {})) {
      const cod = Number(produto);
      if (Number.isFinite(cod)) byProduto.set(cod, row);
    }
  }
  return byProduto;
};

/** Volta ao array plano `{ SEQPESSOA, SEQPRODUTO, ... }` que o formato do arquivo trazia. */
export const hydrateUltimaCompra = async (): Promise<any[]> => {
  const porCliente = await ultimaCompraPorCliente();
  const out: any[] = [];
  for (const porProduto of porCliente.values()) out.push(...Object.values(porProduto || {}));
  return out;
};

export const hydrateDicionarioProdutos = async (): Promise<Record<number, ProductProps>> => {
  const [porCodigo, precoByProduto, ultimaCompraByProduto] = await Promise.all([
    catalogoPorCodigo(),
    readPrecoByProduto(),
    readUltimaCompraByProduto(),
  ]);

  const dict: Record<number, ProductProps> = {};
  for (const [cod, linhas] of porCodigo) {
    for (const item of linhas) {
      const product = buildProduct(item, precoByProduto.get(cod) ?? [], ultimaCompraByProduto.get(cod));
      dict[cod] = dict[cod] ? mergeProducts(dict[cod], product) : product;
    }
  }

  return dict;
};

// -------------------------------------------------------------------------------------------
// clientes
// -------------------------------------------------------------------------------------------

export const hydrateCliente = async (): Promise<ClientProps[]> => {
  const [rows, ciclo] = await Promise.all([readFlatRows('cliente'), cicloPorCliente()]);

  const grouped = ensureClientsGroupedByLoja(rows) as ClientProps[];

  const withCycle = grouped.map((cliente) => ({
    ...cliente,
    UNILEVER: (cliente as any).FL_UNILEVER === 'True',
    CYCLE: (ciclo.get(Number(cliente.COD_CLIENTE)) || []) as SalesCycleProps[],
  }));

  return withCycle.sort((a, b) => {
    if (a.NM_FANTASIA > b.NM_FANTASIA) return 1;
    if (a.NM_FANTASIA < b.NM_FANTASIA) return -1;
    return 0;
  });
};

// -------------------------------------------------------------------------------------------
// categorias
// -------------------------------------------------------------------------------------------

/** Categoria sintética da campanha, que o processamento antigo prependia à lista. */
const mountCampanhaCategory = (campanha: any): CategoryProps[] => {
  if (!campanha?.nome) return [];
  return [
    {
      NRO_HIERARQUIA: 1,
      DE_CATEGORIA: campanha.nome,
      IMAGE: campanha?.inativo?.selo || '',
      PATH_CATEGORIA: campanha.url_amigavel,
    },
  ] as CategoryProps[];
};

export const hydrateCategoria = async (): Promise<CategoryProps[]> => {
  const [categorias, campanhaProduto] = await Promise.all([
    readFlatRows<CategoryProps>('categoria'),
    hydrateSingleton('campanha_produto'),
  ]);

  const jaTemCampanha = categorias.some((c) => c?.PATH_CATEGORIA === (campanhaProduto as any)?.url_amigavel);
  return [...(jaTemCampanha ? [] : mountCampanhaCategory(campanhaProduto)), ...categorias];
};

/** Dicionário só das categorias reais — a sintética da campanha não entrava aqui. */
export const hydrateDicionarioCategorias = async (): Promise<Record<number, CategoryProps>> => {
  const categorias = await readFlatRows<CategoryProps>('categoria');
  const dict: Record<number, CategoryProps> = {};
  for (const categoria of categorias) {
    const cod = Number(categoria?.COD_CATEGORIA);
    if (!Number.isFinite(cod) || dict[cod]) continue;
    dict[cod] = categoria;
  }
  return dict;
};

// -------------------------------------------------------------------------------------------
// demais formatos
// -------------------------------------------------------------------------------------------

/** Stores de registro único (`campanha_produto`, `campanha_cliente`). */
export const hydrateSingleton = (store: BfSellerStore): Promise<Record<string, any>> => readSingleObject(store);

const mapFromCache = <T>(cache: Map<number, T>): Record<number, T> => {
  const out: Record<number, T> = {};
  for (const [id, valor] of cache) out[id] = valor;
  return out;
};

export const hydrateEventos = async (): Promise<Record<number, any>> => {
  const clientes = await readClienteNodes('cliente_boletos');
  const out: Record<number, any> = {};
  for (const node of clientes) {
    const cod = Number(node?.cod_cliente ?? node?.COD_CLIENTE);
    if (Number.isFinite(cod)) out[cod] = node;
  }
  return out;
};

export const hydrateClienteBoletos = async (): Promise<{ clientes: any[] }> => ({
  clientes: await readClienteNodes('cliente_boletos'),
});

export const hydrateClienteCortes = async (): Promise<{ clientes: any[] }> => ({
  clientes: await readClienteNodes('cliente_cortes'),
});

export const hydratePrecoPromocaoCliente = async (): Promise<Record<number, any>> =>
  mapFromCache(await precoPromocaoPorCliente());

/** O mapa `{ [pessoa]: { VLR_ERP } }` já vem normalizado da gravação; aqui só junta os arquivos. */
export const hydrateSaldoFlex = async (): Promise<Record<string, { VLR_ERP: number | null }>> => {
  const rows = await readValues<Record<string, { VLR_ERP: number | null }>>('saldo_flex');
  const out: Record<string, { VLR_ERP: number | null }> = {};
  for (const mapa of rows) {
    if (isPlainObject(mapa)) Object.assign(out, mapa);
  }
  return out;
};

/** Volta ao array plano `{ COD_CLIENTE, COD_PRODUTO }` que os leitores atuais esperam. */
export const hydrateRestricaoProdutoCliente = async (): Promise<Array<{ COD_CLIENTE: number; COD_PRODUTO: number }>> => {
  const porCliente = await restricoesPorCliente();
  const out: Array<{ COD_CLIENTE: number; COD_PRODUTO: number }> = [];
  for (const [cliente, produtos] of porCliente) {
    for (const produto of produtos) out.push({ COD_CLIENTE: cliente, COD_PRODUTO: Number(produto) });
  }
  return out;
};

/**
 * Array de linhas ou árvore por loja — o `normalizeParametroPrecoBase` aceita os dois formatos,
 * então devolvemos o que veio: lista plana quando todos os arquivos trouxeram array, árvore
 * fundida caso contrário.
 */
export const hydrateParametroPrecoBase = async (): Promise<any> => {
  const values = await readValues('parametro_preco_base');
  if (!values.length) return [];

  if (values.every((value) => Array.isArray(value))) return values.flatMap((value) => value as any[]);

  const tree: Record<string, any> = {};
  for (const value of values) {
    if (isPlainObject(value)) Object.assign(tree, value);
  }
  return tree;
};

export const hydratePagebuilder = async (): Promise<any[]> => {
  const rows = await readFlatRows('pagebuilder');
  return rows.map((item, index) => ({ ...item, ID: toCoded(`${index + 1}`, true, true) }));
};

/** Ciclo de venda plano, como o dataset trazia — o agrupado por cliente vive no `bfSellerCache`. */
export const hydrateCicloVenda = async (): Promise<any[]> => {
  const porCliente = await cicloPorCliente();
  const out: any[] = [];
  for (const linhas of porCliente.values()) out.push(...linhas);
  return out;
};

export const hydrateRecomendacaoBigData = () => readFlatRows('recomendacaoBigData');

/** Stores lidas inteiras (estoque, marcas, combo…): a lista plana dos arquivos. */
export const hydrateRows = (store: BfSellerStore) => readFlatRows(store);
